import React, { useEffect, useState } from 'react';
import { FiArrowUpRight, FiMessageSquare, FiRefreshCw, FiWifiOff } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { axiosClient } from 'api/api';
import { useAuth } from 'context/auth-context';
import { ticketLink } from './useLinkedTicket';
import './UserQueries.css';
import './SupportNotifications.css';

type Notification = { source: 'concern' | 'feedback'; ticket_id: number; unread_count: number };
type Props = {
  children: (count: number, content: React.ReactNode) => React.ReactNode;
  /** Called when a notification opens its ticket page, so the host can close its popover. */
  onOpenTicket?: () => void;
};
function Inbox({ admin, children, onOpenTicket }: Props & { admin: boolean }) {
  const navigate = useNavigate();
  const base = `/api/${admin ? 'admin/user-queries' : 'my-queries'}`;
  const [data, setData] = useState<{ unread_count: number; tickets: Notification[] }>({ unread_count: 0, tickets: [] });
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    let active = true;
    let pending = false;
    const refresh = async () => {
      if (pending || document.hidden) return;
      pending = true;
      if (active) setRefreshing(true);
      try {
        const response = await axiosClient.get(`${base}/notifications`);
        if (active) { setData(response.data.result); setError(''); setHasLoaded(true); }
      } catch { if (active) setError('Unable to refresh support notifications.'); }
      finally { pending = false; if (active) setRefreshing(false); }
    };
    void refresh();
    const timer = window.setInterval(refresh, 15000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('support-messages-read', refresh);
    return () => {
      active = false; window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('support-messages-read', refresh);
    };
  }, [base, refreshKey]);
  // Open the ticket on its own page (User Queries / My queries), which shows the conversation there.
  const show = (item: Notification) => {
    onOpenTicket?.();
    navigate(ticketLink(admin, item.source, item.ticket_id));
  };
  // "Empty" only after a successful check; otherwise show the loading/error states below so a failing
  // notifications API (e.g. queryNotifications.sql not applied) is visible instead of looking like no messages.
  const content = hasLoaded && !error && !data.tickets.length ? <p className="py-10 text-center text-sm text-ink-3">No notification available</p> : <section aria-label="Support notifications" className="support-inbox">
      <div className="support-inbox-heading"><div><h3>Conversations</h3><p>Updates from {admin ? 'your members' : 'our support team'}</p></div>
        <button className="zs-icon-btn" type="button" aria-label="Refresh support notifications" disabled={refreshing} onClick={() => setRefreshKey(value => value + 1)}><FiRefreshCw className={refreshing ? 'animate-spin' : ''} /></button>
      </div>
      {error && <div role="alert" className="support-inbox-state">
        <span className="support-state-icon support-state-error"><FiWifiOff /></span><h4>Updates couldn’t load</h4><p>{hasLoaded ? 'Your last updates are shown below. Try refreshing for the latest messages.' : 'We couldn’t check for new messages. Please try again in a moment.'}</p>
        <button type="button" className="zs-btn zs-btn-secondary zs-btn-sm" disabled={refreshing} onClick={() => setRefreshKey(value => value + 1)}><FiRefreshCw />{refreshing ? 'Retrying…' : 'Try again'}</button>
      </div>}
      {!hasLoaded && refreshing && !error && <div className="support-inbox-state" role="status"><span className="support-state-icon"><FiRefreshCw className="animate-spin" /></span><h4>Checking for updates</h4><p>Your conversations will appear here.</p></div>}
      <div className="support-inbox-list">{data.tickets.map(item => <button key={`${item.source}-${item.ticket_id}`} type="button" className="support-message-card" onClick={() => show(item)}>
        <span className="support-message-icon"><FiMessageSquare /></span><span className="support-message-copy"><strong>{admin ? 'New member reply' : 'Support replied to you'}</strong><span>{item.source.toUpperCase()}-{item.ticket_id}</span><small>{item.unread_count} unread {Number(item.unread_count) === 1 ? 'message' : 'messages'}</small></span><span className="support-unread-dot" aria-label="Unread" />
      </button>)}</div>
      <Link className="support-inbox-footer" to={admin ? '/admin/user-queries' : '/my-queries'}>View all queries <FiArrowUpRight /></Link>
    </section>;
  return <>{children(data.unread_count, content)}</>;
}
export function SupportNotifications({ children, onOpenTicket }: Props) {
  const { auth, roleLoading } = useAuth();
  if (!auth?.id || roleLoading) return <>{children(0, null)}</>;
  return <Inbox key={`${auth.id}-${auth.role}`} admin={Number(auth.role) === 2} onOpenTicket={onOpenTicket}>{children}</Inbox>;
}
